import { forwardRef } from 'react';
import {
  type AccessibilityRole,
  Text as RNText,
  type TextProps as RNTextProps,
  StyleSheet,
} from 'react-native';
import { type TypographyVariant, typography } from '../../theme/typography';

const HEADING_VARIANTS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

interface TextProps extends RNTextProps {
  variant?: TypographyVariant;
  color?: string;
  align?: 'left' | 'center' | 'right' | 'justify';
  weight?: 'light' | 'normal' | 'medium' | 'semibold' | 'bold' | 'extrabold' | 'black';
  size?: 'xs' | 'sm' | 'base' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  italic?: boolean;
  underline?: boolean;
  uppercase?: boolean;
  lowercase?: boolean;
  capitalize?: boolean;
  lineThrough?: boolean;
}

const fontWeightMap = {
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  extrabold: '800',
  black: '900',
} as const;

const fontSizeMap = {
  xs: 11,
  sm: 12,
  base: 14,
  md: 16,
  lg: 18,
  xl: 20,
  '2xl': 24,
  '3xl': 30,
  '4xl': 36,
  '5xl': 48,
} as const;

export const Text = forwardRef<RNText, TextProps>(
  (
    {
      variant = 'body',
      color,
      align,
      weight,
      size,
      italic,
      underline,
      uppercase,
      lowercase,
      capitalize,
      lineThrough,
      style,
      children,
      ...rest
    },
    ref,
  ) => {
    const baseStyle = typography[variant];

    const computedStyle = StyleSheet.flatten([
      baseStyle,
      {
        color: color || baseStyle.color,
        textAlign: align,
        fontWeight: weight ? fontWeightMap[weight] : undefined,
        fontSize: size ? fontSizeMap[size] : undefined,
        fontStyle: italic ? 'italic' : undefined,
        textDecorationLine: (() => {
          const decorations = [];
          if (underline) decorations.push('underline');
          if (lineThrough) decorations.push('line-through');
          return decorations.length > 0 ? decorations.join(' ') : undefined;
        })(),
        textTransform: (() => {
          if (uppercase) return 'uppercase';
          if (lowercase) return 'lowercase';
          if (capitalize) return 'capitalize';
          return undefined;
        })(),
      },
      style,
    ]);

    const inferredRole: AccessibilityRole | undefined = HEADING_VARIANTS.has(variant)
      ? 'header'
      : undefined;

    return (
      <RNText
        ref={ref}
        style={computedStyle as any}
        accessibilityRole={rest.accessibilityRole ?? inferredRole}
        {...rest}
      >
        {children}
      </RNText>
    );
  },
);

Text.displayName = 'Text';

// Convenience components for common text variants
export const Heading1 = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="h1" {...props} />
));
Heading1.displayName = 'Heading1';

export const Heading2 = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="h2" {...props} />
));
Heading2.displayName = 'Heading2';

export const Heading3 = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="h3" {...props} />
));
Heading3.displayName = 'Heading3';

export const Heading4 = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="h4" {...props} />
));
Heading4.displayName = 'Heading4';

export const Heading5 = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="h5" {...props} />
));
Heading5.displayName = 'Heading5';

export const Heading6 = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="h6" {...props} />
));
Heading6.displayName = 'Heading6';

export const Body = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="body" {...props} />
));
Body.displayName = 'Body';

export const Caption = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="caption" {...props} />
));
Caption.displayName = 'Caption';

export const Label = forwardRef<RNText, Omit<TextProps, 'variant'>>((props, ref) => (
  <Text ref={ref} variant="label" {...props} />
));
Label.displayName = 'Label';
